import { Text, Image, useSitecoreContext, LayoutServicePageState } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import { useI18n } from 'next-localization';
import mockData from './MockData/mock.data.event-listing.json';
import { EventListingResponse } from './Types/EventListingResponse.Type';
import { EventListingFields, EventCard, OccurrenceDateTime, EventTypeData, Filter } from './Types/EventListing.Type';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';
import { fixMediaImagePath, getCleanID, getLanguageBasedUrl } from 'lib/utils';
import { fetchEvents, filterEventsByDate } from './EventListingUtils';

import styles from './EventListing.module.scss';
import Icon from 'components/helpers/Icon';
import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import EventListingFilters from './EventListingFilter';
import { isBefore, startOfToday } from 'date-fns';

type EventListingProps = ComponentProps & {
  fields: EventListingFields;
};

const cx = classNames.bind(styles);

const EventListing = (props: EventListingProps): React.JSX.Element => {
  const { className, fields } = props;
  const [events, setEvents] = useState<EventListingResponse[]>([]);
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const [showNoResultsFound, setShowNoResultsFound] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [eventCards, setEventCards] = useState<EventCard[]>([]);
  const [eventCardsOnPageLoad, setEventCardsOnPageLoad] = useState<EventCard[]>([]);
  const hasFetchedOnload = useRef(false);
  const hasSetInitialCards = useRef(false);
  const [shouldQuery, setShouldQuery] = useState<boolean>(true);
  const [dateFilter, setDateFilter] = useState<string>();

  //Build Event Cards
  useEffect(() => {
    const cards: EventCard[] = [];
    const today = startOfToday();
    const eventsToShow = fields.eventsToShow || 'Current';

    if (!events || events.length === 0) return;

    events.forEach((event) => {
      const occurrences = event.children?.EventOccurrences || [];
      const groupedOccurrences: { [key: string]: OccurrenceDateTime[] } = {};

      occurrences.forEach((occ) => {
        const startDate = occ?.startDateTime?.jsonValue?.value;
        if (!startDate || startDate === '0001-01-01T00:00:00Z') return;

        const date = new Date(startDate);
        const groupKey = date.toLocaleDateString('en-CA').split('T')[0]; //Format: 'YYY-MM-DD' eg: '2025-05-30'

        const endDateRaw = occ?.endDateTime?.jsonValue?.value;
        const endDate = endDateRaw && endDateRaw !== '0001-01-01T00:00:00Z' ? new Date(endDateRaw) : date;

        if (!groupedOccurrences[groupKey]) {
          groupedOccurrences[groupKey] = [];
        }

        groupedOccurrences[groupKey].push({
          id: occ.id,
          time: date
            .toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
            })
            .replace('AM', 'a.m.')
            .replace('PM', 'p.m.'),
          date: date,
          endDate: endDate,
          isCanceled: occ.canceled.boolValue,
        });
      });

      Object.entries(groupedOccurrences).forEach(([groupKey, times]) => {
        const sortedTimes = times.sort((a, b) => a.date.getTime() - b.date.getTime());

        cards.push({
          id: `${event.id}_${groupKey}`,
          event,
          date: sortedTimes[0].date,
          endDate: sortedTimes[0].endDate,
          formattedDate: sortedTimes[0].date.toLocaleDateString(sitecoreContext.language ?? 'en', {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
          }),
          times: sortedTimes,
          isFeatured: false,
        });
      });
    });

    const pastOrCurrentCards = cards.filter((card) => {
      if (eventsToShow === 'Current') return !isBefore(card.endDate, today);
      if (eventsToShow === 'Past') return isBefore(card.endDate, today);
      return true;
    });

    pastOrCurrentCards.forEach((card) => {
      const cardId = getCleanID(card.id.split('_')[0]); //remove group key
      card.isFeatured = fields.featuredEventIds.some((featuredId) => getCleanID(featuredId) === cardId);
    });

    let sortedCards: EventCard[] = [];

    if (!shouldQuery) {
      sortedCards = [...pastOrCurrentCards].sort((a, b) =>
        fields.sortDescending ? b.date.getTime() - a.date.getTime() : a.date.getTime() - b.date.getTime()
      );
      setEventCards(sortedCards);
      setShowNoResultsFound(sortedCards.length === 0);
      if (dateFilter) {
        const datefilteredEventCards = filterEventsByDate(sortedCards, dateFilter);
        setEventCards(datefilteredEventCards);
        setShowNoResultsFound(datefilteredEventCards.length === 0);
      }
    }

    //On Intial Page Load
    if (!hasSetInitialCards.current) {
      // Sort featured cards on top
      sortedCards = [...pastOrCurrentCards].sort((a, b) => {
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
        return fields.sortDescending ? b.date.getTime() - a.date.getTime() : a.date.getTime() - b.date.getTime();
      });
      setEventCards(sortedCards);
      setEventCardsOnPageLoad(sortedCards);
      hasSetInitialCards.current = true;
      setShowNoResultsFound(sortedCards.length === 0);
      if (dateFilter) {
        const datefilteredEventCards = filterEventsByDate(sortedCards, dateFilter);
        setEventCards(datefilteredEventCards);
        setShowNoResultsFound(datefilteredEventCards.length === 0);
      }
    }
  }, [events, fields, sitecoreContext.language, shouldQuery, dateFilter]);

  //OnLoad Event Listing Fetch
  useEffect(() => {
    const isNormalPageMode = sitecoreContext.pageState === LayoutServicePageState.Normal;
    const shouldLoad = (!isNormalPageMode || shouldQuery) && !hasFetchedOnload.current;
    if (!shouldLoad) return;
    if (isNormalPageMode) {
      const dynamicFilters: Filter[] = [];
      const audienceFilters: Filter[] = [];
      const onDemandLocationFilter = [
        { name: 'locationType', value: getCleanID('72E84054-9027-4AA3-8659-6430EAF72C68'), operator: 'NCONTAINS' },
      ]; //Exclude On Demand Events
      const locationFilters: Filter[] = [];
      const defaultLocationTypes = [
        {
          id: '{9E98FB3F-20DF-40EF-BC98-FE8D6708590E}',
          key: 'Online',
          phrase: 'Online',
        },
        {
          id: '{F1F59A94-BE62-46A9-94C9-56B0D329EF23}',
          key: 'In Person',
          phrase: 'In Person',
        },
      ];
      const audience = fields.audienceFilters;
      const locationTypes = fields.locationTypeFilters.length > 0 ? fields.locationTypeFilters : defaultLocationTypes;

      audience?.forEach((type) => {
        const typeId = getCleanID(type?.id);
        if (typeId) {
          audienceFilters.push({ name: 'audienceSelected', value: typeId, operator: 'CONTAINS' });
        }
      });

      locationTypes?.forEach((type) => {
        const typeId = getCleanID(type?.id);
        if (typeId) {
          locationFilters.push({ name: 'locationType', value: typeId, operator: 'CONTAINS' });
        }
      });
      if (audienceFilters?.length) {
        dynamicFilters.push({ OR: audienceFilters });
      }
      if (locationFilters?.length) {
        dynamicFilters.push({ OR: locationFilters });
      }
      if (onDemandLocationFilter?.length) {
        dynamicFilters.push({ OR: onDemandLocationFilter });
      }
      fetchEvents({
        language: sitecoreContext.language,
        dynamicFilters: dynamicFilters,
        onSuccess: (data) => {
          setEvents(data);
          setShowLoader(false);
          setShowNoResultsFound(data.length === 0);
          hasFetchedOnload.current = true;
        },
        onError: () => {
          setEvents([]);
          setShowLoader(false);
          setShowNoResultsFound(false);
          hasFetchedOnload.current = true;
        },
      });
    } else {
      setEvents(mockData as unknown as EventListingResponse[]);
      setShowLoader(false);
      setShowNoResultsFound((mockData as unknown as EventListingResponse[]).length === 0);
      hasFetchedOnload.current = true;
    }
  }, [fields, setEvents, sitecoreContext, shouldQuery]);

  const replaceToken = (text?: string): string => {
    const count = showNoResultsFound ? '0' : eventCards.length?.toString();
    return text?.replace('{count}', count) ?? '';
  };

  const toggleMoreTimes = (key: string) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getUrlForEventId = (card: EventCard) => {
    return getLanguageBasedUrl(
      `${props.fields.eventDetailPageUrl}?eventId=${card?.event?.id}&occurrenceId=${card?.times[0]?.id}`,
      sitecoreContext.language
    );
  };

  const handleEventDetail = async (_e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, card: EventCard) => {
    window.location.href = getUrlForEventId(card);
    return false;
  };

  const paginationButtonClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    const target = e.target as HTMLButtonElement;
    const pageIndex = Number(target.dataset['pageIndex']);
    setCurrentPageIndex(pageIndex);
  };

  const previousButtonClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    setCurrentPageIndex(currentPageIndex - 1);
  };

  const nextButtonClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const pageSize = +(fields.numberOfResultsToDisplay || 10);
  const totalPages = Math.ceil(eventCards?.length / pageSize);
  const startItemIndex = currentPageIndex === 0 ? currentPageIndex : currentPageIndex * pageSize;
  const endItemIndex = startItemIndex === 0 ? pageSize : startItemIndex + pageSize;

  const handleDateFilterChange = (dateFilter: string) => {
    setDateFilter(dateFilter);
    setShouldQuery(false);
  };

  const handleFilterChange = useCallback(
    (eventTypes: EventTypeData[], locationTypes: EventTypeData[]) => {
      const dynamicFilters: Filter[] = [];
      const audienceFilters: Filter[] = [];
      const typeFilters: Filter[] = [];
      const locationFilters: Filter[] = [];
      const audience = fields.audienceFilters;
      const preFilter = fields.locationTypeFilters || [];
      const uiFilter = locationTypes || [];
      const combinedLocationTypes =
        uiFilter.length > 0
          ? preFilter.filter((type) => uiFilter.some((ui) => ui.id === getCleanID(type?.id)))
          : preFilter;
      const onDemandLocationFilter = [
        { name: 'locationType', value: getCleanID('72E84054-9027-4AA3-8659-6430EAF72C68'), operator: 'NCONTAINS' },
      ]; //Exclude On Demand Events

      audience?.forEach((type) => {
        const typeId = getCleanID(type?.id);
        if (typeId) {
          audienceFilters.push({ name: 'audienceSelected', value: typeId, operator: 'CONTAINS' });
        }
      });

      eventTypes?.forEach((type) => {
        const typeId = type?.id;
        if (typeId) {
          typeFilters.push({ name: 'type', value: typeId, operator: 'CONTAINS' });
        }
      });

      combinedLocationTypes?.forEach((type) => {
        const typeId = getCleanID(type?.id);
        if (typeId) {
          locationFilters.push({ name: 'locationType', value: typeId, operator: 'CONTAINS' });
        }
      });
      if (audienceFilters?.length) {
        dynamicFilters.push({ OR: audienceFilters });
      }
      if (locationFilters?.length) {
        dynamicFilters.push({ OR: locationFilters });
      }
      if (typeFilters?.length) {
        dynamicFilters.push({ OR: typeFilters });
      }
      if (onDemandLocationFilter?.length) {
        dynamicFilters.push({ OR: onDemandLocationFilter });
      }

      fetchEvents({
        language: sitecoreContext.language,
        dynamicFilters: dynamicFilters,
        onSuccess: (data) => {
          setEvents(data);
          setShowLoader(false);
          setShowNoResultsFound(data.length === 0);
          setCurrentPageIndex(0);
          setShouldQuery(false);
        },
        onError: () => {
          setEvents([]);
          setShowLoader(false);
          setShowNoResultsFound(false);
        },
      });
    },
    [sitecoreContext.language, fields.audienceFilters, fields.locationTypeFilters]
  );

  return (
    <div className={cx('event-listing', 'component-container-edge', className)}>
      {showLoader && (
        <div role="status">
          <span className={cx('screen-reader-only')}>{t('Event listing loaded')}</span>
          <Loader variant={LoaderVariant.Spinner} />
        </div>
      )}
      {!showLoader && !(eventCardsOnPageLoad?.length > 0) && (
        <div role="status" className={cx('event-listing-no-filters')}>
          <div className={cx('header-row')}>
            <Text encode={false} field={{ value: replaceToken(fields.title) }} tag="h2" />
            <div role="status" aria-live="polite" aria-atomic="true">
              <span className={cx('screen-reader-only')}>{replaceToken(fields.title)}</span>
            </div>
            <span className={cx('date-sort')}>{t('SortedByDate')}</span>
          </div>

          <div className={cx('no-events')}>
            <div className={cx('icon-wrapper')}>
              <Icon variant="calendar-in-circle" />
            </div>
            <strong>{t('NoEventFound')}</strong>
          </div>
        </div>
      )}
      <div className={cx('event-listing-with-filters')}>
        {!showLoader && eventCardsOnPageLoad?.length > 0 && (
          <>
            <EventListingFilters
              eventsToShow={fields.eventsToShow}
              eventCards={eventCardsOnPageLoad}
              onFilterChangeCallback={handleFilterChange}
              onDateFilterChangeCallback={handleDateFilterChange}
              showNoResultsFound={showNoResultsFound}
              locationTypeFilters={fields.locationTypeFilters}
              headerTitle={replaceToken(fields.title)}
            />
          </>
        )}

        {!showLoader && !showNoResultsFound && eventCards?.length > 0 && (
          <div className={cx('listing', 'event-div')}>
            {eventCards?.slice(startItemIndex, endItemIndex.valueOf()).map((card) => {
              const isExpanded = expanded[card.id];
              const shownTimes = isExpanded ? card.times : card.times.slice(0, 2);
              const remainingTimes = card.times.length - shownTimes.length;

              const isValidImage =
                card.event.image?.jsonValue?.value && Object.keys(card.event.image?.jsonValue?.value).length > 0;
              const eventImage = isValidImage
                ? card.event.image?.jsonValue
                : fixMediaImagePath(fields?.eventFallbackImage);

              return (
                <div key={card.id} className={cx('event-card')}>
                  <div className={cx('event-listing-image-column')}>
                    <Image field={eventImage} className={cx('event-listing-image-column__image')} />
                  </div>
                  <div className={cx('event-listing-content-column')}>
                    {card.isFeatured && <span className={cx('featured-tag')}>Featured Event</span>}
                    <a
                      href={getUrlForEventId(card)}
                      onClick={(e) => {
                        e.preventDefault();
                        handleEventDetail(e, card);
                      }}
                      className={cx('event-detail-link')}
                    >
                      <Text encode={sitecoreContext.pageEditing} field={card.event.name} tag="h3" />
                      <Icon variant="carat" />
                    </a>
                    <div className={cx('occurrence-date-time')}>
                      <ul className={cx('occurrence-times')}>
                        <li>{card.formattedDate}</li>
                        {shownTimes.map((t, i) => (
                          <li key={t.id}>
                            <span className={cx('occurrence-time-span')}>
                              {t.isCanceled && <Icon variant="error" />}
                              {t.time}
                            </span>
                            {(i < shownTimes.length - 1 || remainingTimes > 0) && <span>|</span>}
                          </li>
                        ))}
                        {remainingTimes > 0 && !isExpanded && (
                          <li>
                            <a onClick={() => toggleMoreTimes(card.id)} className={cx('toggle-more-times')}>
                              +{remainingTimes} {t('More')}
                            </a>
                          </li>
                        )}
                        {isExpanded && (
                          <li>
                            <a onClick={() => toggleMoreTimes(card.id)} className={cx('toggle-more-times')}>
                              {t('ShowLess')}
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                    <Text
                      tag="p"
                      encode={sitecoreContext.pageEditing}
                      field={card.event.inPersonEventLocation?.targetItem?.locationName}
                      className={cx('small')}
                    />
                    {card.event.locationType?.targetItems?.some((item) => item.name === 'Online') && (
                      <div className={cx('icon-wrapper')}>
                        <Icon variant="video" /> <span>{t('OnlineEvent')} </span>
                      </div>
                    )}
                    <Text tag="p" field={card.event.type?.targetItem?.eventType} className={cx('event-type')} />
                  </div>
                </div>
              );
            })}

            {totalPages > 1 && (
              <div className={cx('pagination-container')}>
                <Button
                  className={cx('previous-button')}
                  variant={ButtonVariant.Anchor}
                  disabled={currentPageIndex == 0}
                  onClick={previousButtonClick}
                >
                  <Icon variant="carat" /> {t('Previous')}
                </Button>
                <div className={cx('pagination-numeric-button')}>
                  {Array.from({ length: totalPages }, (_v, i) => (
                    <Button
                      variant={ButtonVariant.Anchor}
                      data-page-index={i}
                      key={`page-${i}`}
                      onClick={paginationButtonClick}
                    >
                      {i + 1}
                    </Button>
                  ))}
                </div>
                <Button
                  className={cx('next-button')}
                  variant={ButtonVariant.Anchor}
                  disabled={currentPageIndex >= totalPages - 1}
                  onClick={nextButtonClick}
                >
                  {t('Next')} <Icon variant="carat" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default compose<EventListingProps>(withStyles())(EventListing);
